import { useCallback } from "react";
import { useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { WordListType } from "src/model/enum/enums";
import { WordList } from "../../model/WordList";
import { learningActions } from '../../store/slices/learningSlice';
import { appDispatch, RootState } from '../../store/store';
import useFetch from "../api/useFetch";
import { useCustomList } from "./useCustomList";

export const useWordList = () => {
    const { send } = useFetch<any>();
    const ac = bindActionCreators(learningActions, appDispatch);
    const { learningInfo } = useSelector((state: RootState) => state.learning);
    const { removeCustomList, isDefault } = useCustomList();



    const fetchWordListData = useCallback(async (wordList: WordList, updateStore: boolean = true) => {
        if (!wordList?.WordsUrl) return null;

        const { response } = await send(wordList.WordsUrl);
        const wordListData = response?.uw
            ?.split(',')
            ?.map((id: string) => parseInt(id.trim(), 10))
            ?.filter((id: number) => !isNaN(id)) ?? [];

        if (learningInfo && updateStore) {
            const updatedWordLists = learningInfo.WordLists.map(wl =>
                wl.ID === wordList.ID ? { ...wl, WordListData: wordListData } : wl
            );
            ac.updateLearning({ WordLists: updatedWordLists });
        }
        return wordListData as number[];
    }, [send, ac, learningInfo]);

    const updateWordListStats = useCallback(async (wordList: WordList) => {
        if (!wordList) return;
        if (wordList.Type === WordListType.Custom &&
            wordList.CustomListData.length === 0
            && !isDefault(wordList)) {
            removeCustomList(wordList);
            return;
        }

        let updatedWordlist: WordList;
        if (wordList.Type === WordListType.All) {
            const known = learningInfo?.KnownWordIds?.length || 0;
            const unknown = learningInfo?.UnknownWordIds?.length || 0;
            const total = 24241; // Total vocabulary count
            const uncovered = known + unknown;
            const isCompleted = uncovered >= total;
            const uncoveredCountText = isCompleted ? '' :
                `${Math.round((uncovered / total) * 100)}%`;

            updatedWordlist = {
                ...wordList,
                KnownCount: known,
                UnknownCount: unknown,
                TotalCount: total,
                UncoveredCountText: uncoveredCountText,
                IsCompleted: isCompleted
            };
        } else if (wordList.Type === WordListType.Custom) {
            const total = wordList.CustomListData.length || 0;
            const known = learningInfo?.KnownWordIds?.filter
                (x => wordList.CustomListData?.some(w => w.WordId === x)).length || 0;
            const unknown = learningInfo?.UnknownWordIds?.filter
                (x => wordList.CustomListData?.some(w => w.WordId === x)).length || 0;
            const uncovered = known + unknown;
            const isCompleted = uncovered >= total;

            const uncoveredCountText = isCompleted ? '' :
                `${Math.round((uncovered / total) * 100)}%`;


            // console.log('wordList', wordList.CustomListData)
            // console.log('known', known)
            // console.log('unknown', unknown)
            // console.log('total', total)
            // console.log('uncovered', uncovered)
            // console.log('isCompleted', isCompleted)

            updatedWordlist = {
                ...wordList,
                KnownCount: known,
                UnknownCount: unknown,
                TotalCount: total,

                UncoveredCountText: uncoveredCountText,
                IsCompleted: isCompleted
            };
        }

        else {
            const wordListData = await fetchWordListData(wordList);
            const total = wordListData?.length || 0;
            const known = learningInfo?.KnownWordIds?.filter(x => wordListData?.includes(x)).length || 0;
            const unknown = learningInfo?.UnknownWordIds?.filter(x => wordListData?.includes(x)).length || 0;
            const uncovered = known + unknown;
            const isCompleted = uncovered >= total;


            const uncoveredCountText = isCompleted ? '' :
                `${Math.round((uncovered / total) * 100)}%`;

            updatedWordlist = {
                ...wordList,
                KnownCount: known,
                UnknownCount: unknown,
                TotalCount: total,
                UncoveredCountText: uncoveredCountText,
                IsCompleted: isCompleted
            };
        }

        const updatedWordLists = learningInfo?.WordLists.map(wl =>
            wl.ID === wordList.ID ? updatedWordlist : wl
        );
        ac.updateLearning({ WordLists: updatedWordLists });
    }, [learningInfo, ac, fetchWordListData, removeCustomList, isDefault]);

    const fetchAllWordListData = useCallback(async (wordLists: WordList[]) => {
        const fetchPromises = wordLists.map(async (wordList) => {
            if (!wordList.WordsUrl) return wordList;

            const { response } = await send(wordList.WordsUrl);
            if (wordList.Type === WordListType.Subject) {
                const wordListData = response?.uw
                    ?.split(',')
                    ?.map((id: string) => parseInt(id.trim(), 10))
                    ?.filter((id: number) => !isNaN(id)) ?? [];

                return { ...wordList, WordListData: wordListData };
            }
            else if (wordList.Type === WordListType.Custom) {
                const customListData = response?.uw || [];
                return { ...wordList, CustomListData: customListData };
            }
            return wordList;
        });

        const updatedWordLists = await Promise.all(fetchPromises);
        ac.updateLearning({ WordLists: updatedWordLists });
    }, [send, ac]);

    return {
        learningInfo,
        updateWordListStats,
        fetchWordListData,
        fetchAllWordListData
    };
};